import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import GraphView from '../graph/GraphView';
import DonutView from '../donut/DonutView';
import SummaryOne from './SummaryOne';
import { CardMonitor, TypeSelection } from './utils';
import {
    getDonut, getSummary, analyzeParams, getGraph, getSpeed,
    clearAnalyzeData, subscribeAnalyzeKPI, unsubscribeAnalyzeKPI, getTotalLoadInfo, getTotalTareInfo,
} from '../../actions/analyze';
import { getDateRangeInfo } from '../../actions/analyze';
import { isUUID, isEGView, isPlannedOrTodate, groupBy, isCustomerView } from '../../helpers/common';
import { LOGISTIC_PLAN_TYPE_ID, VIEW_CREW, VIEW_EG, VIEW_PLAN } from '../../constants';
import { Unit } from '../../helpers/unit';
import { getLogisticsHistory } from '../../actions/logistics';

const unit = Unit.getInstance();

const AnalyzeInfoOne = (props) => {
    const dispatch = useDispatch();
    const { analyzeParams: { tab }, elastic: { isRequesting: workedKPI }, donutInfo, graphInfo, summaryInfo, speed } = useSelector((state => state.analyze));
    const { selectedPlanType: selectedPlanTypeId, isSelectAllPlan: isAllPlan } = useSelector((state => state.plan));
    const settings = useSelector((state => state.user.settings));
    const farmId = useSelector((state => state.farm.currentFarm.farm_uuid));
    const loadInfo = useSelector((state => state.analyze.loadCapacityInfo));
    const isRequesting = useSelector((state => state.analyze.loadCapacityInfo.isRequesting));
    const logisticsHistory = useSelector(state => state.logistics.logisticsHistory);

    useEffect(() => { getAnalyzeInfo() }, []);
    useEffect(() => { getAnalyzeInfo() }, [selectedPlanTypeId]);
    useEffect(() => { getAnalyzeInfo() }, [props.params, tab]);
    useEffect(() => { if (props.params) getSearchHistory(props.params, 'load') }, [props.params]);

    useEffect(() => {
        if (loadInfo && loadInfo.loadHistory && isRequesting) {
            getSearchHistory(props.params, 'tare')
        }
    }, [loadInfo, isRequesting]);

    const totalLoadCapacity = useMemo(() => {
        if (!loadInfo || isRequesting) return 0;
        const { loadHistory, tareHistory } = loadInfo

        const isLoadHistory = loadHistory && loadHistory.load && loadHistory.load.total_capacity;
        const istareHistory = tareHistory && tareHistory.load && tareHistory.load.total_capacity;

        if (loadHistory && tareHistory) {
            const loadWeight = (isLoadHistory) ? unit.toRoundOfWeight(isLoadHistory, false) : 0;
            const tareWeight = (istareHistory) ? unit.toRoundOfWeight(istareHistory, false) : 0;
            const netYield = loadWeight - tareWeight;
            return unit.toFixedOf(netYield, 2);
        }

        return 0;
    }, [loadInfo, isRequesting]);

    const getSearchHistory = (params, loadType) => {
        if (!params) return;

        const dateInfo = dispatch(getDateRangeInfo());
        const loadParams = {
            farmId: farmId,
            filter: 'last',
            egId: params.egId ? params.egId : null,
            // isTruck: egId ? isTruck : false,
            regionId: params.regionId ? params.regionId : null,
            view: params.egId ? VIEW_EG : (params.crewId ? VIEW_CREW : VIEW_PLAN),
            planTypeId: selectedPlanTypeId,
            ...dateInfo
        };

        if (params.crewId) {
            loadParams.crewId = params.crewId
        }

        if (loadType === 'load') dispatch(getTotalLoadInfo(loadParams));
        else dispatch(getTotalTareInfo(loadParams));

        //get logistic data for seed variety in seed plant type        
        if (loadType === 'load' && selectedPlanTypeId === 2) {
            const logisticParams = {
                farm_uuid: farmId,
                range_type: dateInfo.range,
                from_date: dateInfo.fromDate,
                to_date: dateInfo.toDate,
                plan_type_id: LOGISTIC_PLAN_TYPE_ID,
                include_events: true
            }
            dispatch(getLogisticsHistory(logisticParams));
        }
    };

    const dataBySeedVariety = useMemo(() => {
        if (!loadInfo || isRequesting || (selectedPlanTypeId && selectedPlanTypeId !== 2)) return [];
        const { loadHistory } = loadInfo;
        const { data } = logisticsHistory;

        // The logistics variety name is now obtained from the load params
        const logisticData = (data && data.history && data.history.length > 0) ?
            data.history.filter((e) => e.target_plan_type_id && e.target_plan_type_id === selectedPlanTypeId)
                .map((e) => {
                    const rawVariety = (e.load_params && e.load_params.variety_name) ? e.load_params.variety_name : '';

                    return {
                        ...e, variety_name: rawVariety ? rawVariety.toString().trim().toLowerCase() : ''
                    }
                }) : [];

        const logisticByVariety = groupBy('variety_name')(logisticData);
        if (loadHistory && loadHistory.history && loadHistory.history.length > 0) {
            let varietyByData = [];
            const loadData = groupBy('seed_variety_name')(
                loadHistory.history.map((e) => {
                    const rawVariety = e.load_params ? e.load_params.variety_name : '';
                    const varietyName = (rawVariety && rawVariety.toString().trim().toLowerCase()) || '';

                    return {
                        ...e,
                        seed_variety_name: varietyName
                    };
                })
            );

            if (loadData) {
                for (const data in loadData) {
                    const logisticCapacity = logisticByVariety[data] ? logisticByVariety[data].reduce((a, b) => a + b.load_capacity, 0) : 0;
                    let completedCapacity = 0;
                    let pendingCapacity = 0;
                    const newLoads = (loadData[data] && loadData[data].length > 0) ? loadData[data] : [];

                    if (newLoads && newLoads.length > 0) {
                        newLoads.forEach(load => {
                            const sequenceCapacity = (load.load_sequence.length > 0) ? load.load_sequence.reduce((a, b) => a + b.load_capacity, 0) : (load.load_sequence.length === 0) ? load.load_capacity : 0;
                            if (sequenceCapacity > 0) {
                                completedCapacity += sequenceCapacity;
                            } else {
                                pendingCapacity += load.load_capacity;
                            }
                            if (sequenceCapacity > 0 && load.load_capacity > sequenceCapacity) {
                                pendingCapacity += (load.load_capacity - sequenceCapacity);
                            }
                        });
                    }

                    varietyByData.push({
                        "seedVarietyName": (data === 'null') ? "Unknown" : data,
                        "seedVarietyCount": loadData[data] ? loadData[data].length : 0,
                        "plannedCapacity": logisticCapacity && unit.toRoundOfWeight(logisticCapacity, false),
                        "completedCapacity": unit.toRoundOfWeight(completedCapacity, false),
                        "pendingCapacity": unit.toRoundOfWeight(pendingCapacity, false),
                        // "remainingCapacity": unit.toRoundOfWeight((logisticCapacity - (completedCapacity + pendingCapacity)), false),
                        "remainingCapacity": (logisticCapacity && logisticCapacity > 0) ? unit.toRoundOfWeight((logisticCapacity - (completedCapacity + pendingCapacity)), false) : unit.toRoundOfWeight(((completedCapacity + pendingCapacity)), false)
                    })
                }

                // Find the index of the "Unknown Variety" object
                const unknownVarietyIndex = varietyByData.findIndex((item) => item.seedVarietyName === "Unknown");

                // If "Unknown Variety" exists, move its element to the end
                if (unknownVarietyIndex !== -1) {
                    const unknownVariety = varietyByData.splice(unknownVarietyIndex, 1)[0];
                    varietyByData.push(unknownVariety);
                }
                return varietyByData;
            }
        }
        return [];
    }, [loadInfo, isRequesting, selectedPlanTypeId, logisticsHistory]);

    const getAnalyzeInfo = (isSummary = true) => {
        let { params } = props;

        const reqParams = {
            ...params,
            ...!isAllPlan && { planTypeId: selectedPlanTypeId },
            ...isAllPlan && { planTypeId: params.planTypeId }
        }

        if (isEGView(reqParams.view) && !isUUID(reqParams.crewId)) {
            if (isSummary) dispatch(clearAnalyzeData());
            dispatch(unsubscribeAnalyzeKPI(reqParams));
            if (!isPlannedOrTodate(reqParams.range)) dispatch(getSummary(reqParams));
            dispatch(subscribeAnalyzeKPI(reqParams));
            dispatch(getSpeed(reqParams));
        }
        else {
            if (isSummary) {
                dispatch(getSummary(reqParams));
                dispatch(subscribeAnalyzeKPI(reqParams));
                if (isEGView(reqParams.view) && reqParams.egId && !isCustomerView(reqParams.view)) dispatch(getSpeed(reqParams));
            }
            if (tab === 1 && !summaryInfo.isRequesting && !isCustomerView(reqParams.view)) dispatch(getDonut(reqParams))
            else if (tab === 2 && !summaryInfo.isRequesting && !isCustomerView(reqParams.view)) dispatch(getGraph(reqParams));
        }
    }

    const handleTabSelect = (eventKey) => {
        dispatch(analyzeParams({ tab: eventKey }));
    }

    const isRegionId = props.params.hasOwnProperty("regionId") && props.params.regionId ? true : false;
    return (
        <>
            {!isCustomerView(props.params.view) ? <CardMonitor>
                {
                    !isRegionId && <Row className="m-0">
                        <Col md={12} className="m-0 p-0">
                            <TypeSelection onSelect={(e) => handleTabSelect(e)} activeKey={tab} params={props.params} />
                        </Col>
                    </Row>
                }
                <Row className="m-0">
                    <Col md={12} className="m-0 p-0">
                        {(tab === 1 && props.regionTypeId !== 4) && <DonutView donutData={donutInfo} tab={tab} summary={summaryInfo} params={props.params} />}
                        {tab === 2 && <GraphView graphData={graphInfo} settings={{ areaShortName: settings.areaShortName }} summaryInfo={summaryInfo} tab={tab} />}
                    </Col>
                </Row>
            </CardMonitor> : null}
            <SummaryOne
                donutInfo={donutInfo}
                params={props.params}
                summary={summaryInfo}
                workedKPI={workedKPI}
                speed={speed}
                isRequesting={isRequesting}
                totalLoadCapacity={totalLoadCapacity > 0 ? totalLoadCapacity : 0}
                settings={settings}
                regionTypeId={props.regionTypeId}
                dataBySeedVariety={dataBySeedVariety}
                selectedPlanTypeId={selectedPlanTypeId}
                loadInfo={loadInfo}
            />
        </>
    )
}


export default memo(AnalyzeInfoOne);